import { Button, CheckboxProps } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import React, { useMemo, useCallback } from "react";
import { LoadingButton } from "../../../common/components/LoadingButton";
import { Text } from "../../../common/components/input/Text";
import { Select } from "../../../common/components/input/Select";
import { DatePicker } from "../../../common/components/input/Date";
import { requiredDate, requiredEmail, requiredString } from "../../util/validation";
import { Checkbox } from "../../../common/components/input/Checkbox";
import { HelpPopover } from "../../components/HelpPopover";
import { DataRequestSubmissionMethodEnum } from "../../../common/service/server/Types";
import { Callout, CalloutVariant } from "../../components/Callout";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import {
  PrivacyCenterDto,
  CustomFieldDefinitionDto,
  FieldOptionDto,
} from "../../../common/service/server/dto/PrivacyCenterDto";
import {
  RegulationRequestTypeDto,
  WorkflowType,
  FormType,
  SimplifiedRegionRequestTypeDto,
} from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { isSkipHiddenType, isEmailVerificationRequired } from "../../util/requestTypeUtils";
import { parsePhoneNumber, validatePhoneNumberLength } from "libphonenumber-js";
import { Error as InputError } from "../../copy/input";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { getSensitivePICsWithFilters } from "../../../common/service/resources/data-map/picHelpers";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import { ToolTip } from "@truevault/aquilo";

/**
 * Check if organization collects sensitive PICs for US withdraw consent requests.
 * Similar logic to limit requests - checks if there are sensitive PICs disclosed from non-employment collection groups.
 */
const shouldShowUSWithdrawConsent = (dataMap: DataMapDto | undefined): boolean => {
  if (!dataMap) {
    return false;
  }

  // Get all sensitive PICs (including "Inferences based on sensitive information" for withdraw consent)
  const allSensitivePICIds = getSensitivePICsWithFilters(dataMap, {
    includeInferencesBasedOnSensitiveInformationPIC: true, // Include for withdraw consent requests
  }).map((pic) => pic.id);

  // Check if there are any disclosures involving sensitive PICs from non-employment collection groups
  const hasSensitiveDisclosures = dataMap.disclosure.some((disclosure) => {
    const collectionGroup = dataMap.collectionGroups[disclosure.groupId];
    const pic = dataMap.categories[disclosure.picId];

    if (!collectionGroup || !pic) return false;

    return allSensitivePICIds.includes(pic.id) && collectionGroup.type !== "EMPLOYMENT";
  });

  return hasSensitiveDisclosures;
};

const getDescriptionLabel = (requestType: RegulationRequestTypeDto | undefined): string | null => {
  if (!requestType) return null;

  // Use workflowType enum to determine description label
  if (requestType.workflowType === WorkflowType.CORRECTION) {
    return "What data does the consumer want corrected?";
  }
  if (requestType.workflowType === WorkflowType.OBJECTION) {
    return "What is the consumer's objection to processing?";
  }
  if (requestType.workflowType === WorkflowType.RESTRICT_PROCESSING) {
    return "What are the consumer's legal bases to restrict processing?";
  }

  return null;
};

const isDescriptionRequired = (
  submissionMethod: DataRequestSubmissionMethodEnum,
  sendingEmailVerification: boolean,
): any => {
  return submissionMethod === "Email" && !sendingEmailVerification;
};

const showAddressorPhoneNumberForm = (
  privacyCenter: PrivacyCenterDto,
  addressOrPhoneNumber: "phoneNumber" | "address",
  isYpcForm: boolean,
): boolean => {
  if (addressOrPhoneNumber === "phoneNumber") {
    return isYpcForm
      ? privacyCenter.showPhoneNumberFormForOptOut
      : privacyCenter.showPhoneNumberFormForRequests;
  }
  if (addressOrPhoneNumber === "address") {
    return isYpcForm
      ? privacyCenter.showExtendedAddressFormForOptOut
      : privacyCenter.showExtendedAddressFormForRequests;
  }
  return false;
};

interface RequestFormValues {
  subjectFirstName?: string;
  subjectLastName?: string;
  subjectEmailAddress?: string;
  submissionMethod?: DataRequestSubmissionMethodEnum;
  dataRequestType?: RegulationRequestTypeDto;
  description?: string;
  requestDate?: Date;
  sendEmailVerification?: boolean;
  appealId?: string;
  region?: string;
  selectedState?: string;
  hrRequest?: boolean;
  organizationCustomInput?: string;
  subjectPhoneNumber?: string;
  subjectCustomFields?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    // Dynamic custom fields for Crunch integration
    [key: string]: string | undefined;
  };
  // Individual custom field values
  [key: `custom_${string}`]: any;
  // Minor request checkbox state
  isMinorRequest?: boolean;
}

export const newRequestFormValidation = (
  values: RequestFormValues,
  privacyCenter?: PrivacyCenterDto,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const requireDescription =
    isDescriptionRequired(values.submissionMethod, values.sendEmailVerification) &&
    !!getDescriptionLabel(values.dataRequestType);

  requiredString(values, "subjectFirstName", errors);
  requiredString(values, "subjectLastName", errors);
  requiredString(values, "submissionMethod", errors);
  requiredString(values, "dataRequestType", errors);
  requiredEmail(values, "subjectEmailAddress", errors);
  if (values.dataRequestType?.workflowType === WorkflowType.APPEAL) {
    requiredString(values, "appealId", errors);
  }
  requiredDate(values, "requestDate", errors);
  if (requireDescription) {
    requiredString(values, "description", errors);
  }

  // Custom fields are now optional - no validation needed

  // Validate custom fields for Crunch Fitness integration
  if (privacyCenter?.customFields && privacyCenter.customFields.length > 0) {
    // Custom fields should work for any request type, not just DSARs
    if (values.dataRequestType) {
      // Address field codes that should respect showExtendedAddressFormForRequests setting
      const addressFieldCodes = ["address1", "address2", "city", "state", "zipCode", "country"];

      // Determine which flag set to use based on form type (YPC == Opt-Out flows)
      const isYpcForm = values.dataRequestType.formType === FormType.YPC;

      // Filter out address fields based on the correct flag per form type
      let filteredCustomFields = privacyCenter.customFields;
      const showAddress = showAddressorPhoneNumberForm(privacyCenter, "address", isYpcForm);
      if (!showAddress) {
        filteredCustomFields = filteredCustomFields.filter(
          (field) => !addressFieldCodes.includes(field.fieldNameCode),
        );
      }

      // Filter out phone number fields based on the correct flag per form type
      const showPhone = showAddressorPhoneNumberForm(privacyCenter, "phoneNumber", isYpcForm);
      if (!showPhone) {
        filteredCustomFields = filteredCustomFields.filter(
          (field) => field.fieldNameCode !== "phoneNumber",
        );
      }

      // Validate non-minor fields
      const nonMinorFields = filteredCustomFields.filter(
        (field) =>
          field.fieldNameCode !== "minor_first_name" && field.fieldNameCode !== "minor_last_name",
      );

      nonMinorFields.forEach((customField) => {
        const fieldName = `custom_${customField.fieldNameCode}`;
        const fieldValue = values[fieldName];
        const isRequired = customField.required !== false; // Default to true for backward compatibility

        // Special handling for club_location field - only require it if it's shown
        if (customField.fieldNameCode === "club_location") {
          // Helper function to get state abbreviation from location data
          const getStateFromLocationData = (): string | null => {
            const selectedState = values?.selectedState;
            if (!selectedState) return null;

            // Handle US state codes (US-CA -> CA, US-NJ -> NJ, etc.)
            if (typeof selectedState === "string" && selectedState.startsWith("US-")) {
              return selectedState.substring(3); // Remove 'US-' prefix
            }
            return null;
          };

          const selectedState = getStateFromLocationData();
          const selectedStateValue = values?.selectedState;

          // Check if the field has any options available (same logic as rendering component)
          const filteredOptions =
            customField.fieldOptions?.filter((option) => {
              // If no state is selected, no options should be available
              if (!selectedState) {
                return false;
              }

              // If "Other" is selected, show ALL locations
              if (selectedStateValue === "OTHER" || selectedState === "OTHER") {
                return true;
              }

              // Filter options by state
              return option.state && option.state.toUpperCase() === selectedState.toUpperCase();
            }) || [];

          // Only validate club_location if it has options available (field is visible)
          if (filteredOptions.length === 0) {
            return; // Skip validation for this field when it's not visible
          }
        }

        if (isRequired) {
          if (customField.fieldType === "TEXT") {
            const trimmed = fieldValue?.trim?.();
            if (!trimmed || trimmed.length === 0) {
              errors[fieldName] = "Required";
            } else if (trimmed.length > 100) {
              errors[fieldName] = "Maximum characters: 100";
            }
          } else if (customField.fieldType === "DROPDOWN") {
            if (!fieldValue) {
              errors[fieldName] = "Required";
            }
          } else if (customField.fieldType === "PHONE_NUMBER") {
            if (!fieldValue || fieldValue.trim() === "") {
              errors[fieldName] = "Required";
            } else {
              try {
                if (
                  !parsePhoneNumber(fieldValue, "US") ||
                  validatePhoneNumberLength(fieldValue, "US")
                ) {
                  errors[fieldName] = InputError.InvalidPhone;
                }
              } catch (e) {
                errors[fieldName] = InputError.InvalidPhone;
              }
            }
          }
        }
      });

      // Validate minor fields if checkbox is checked
      if (values.isMinorRequest) {
        const minorFields = filteredCustomFields.filter(
          (field) =>
            field.fieldNameCode === "minor_first_name" || field.fieldNameCode === "minor_last_name",
        );

        minorFields.forEach((customField) => {
          const fieldName = `custom_${customField.fieldNameCode}`;
          const fieldValue = values[fieldName];
          const isRequired = customField.required !== false;

          if (isRequired) {
            const trimmed = fieldValue?.trim?.();
            if (!trimmed || trimmed.length === 0) {
              errors[fieldName] = "Required";
            } else if (trimmed.length > 100) {
              errors[fieldName] = "Maximum characters: 100";
            }
          }
        });
      }
    }
  }

  return errors;
};

const SUBMISSION_METHOD_OPTIONS = [
  { label: "Telephone", value: "Telephone" },
  { label: "Email", value: "Email" },
];

// Component for rendering a single custom field based on its type (Crunch Fitness integration)
const CustomFieldComponent = ({
  customField,
  form,
  values,
}: {
  customField: CustomFieldDefinitionDto;
  form: any;
  values: any;
}) => {
  const fieldName = `custom_${customField.fieldNameCode}`;

  // Helper function to get state abbreviation from location data
  const getStateFromLocationData = (): string | null => {
    // For admin requests, state comes from selectedState field
    const selectedState = values?.selectedState;

    if (!selectedState) return null;

    // Handle US state codes (US-CA -> CA, US-NJ -> NJ, etc.)
    if (typeof selectedState === "string" && selectedState.startsWith("US-")) {
      return selectedState.substring(3); // Remove 'US-' prefix
    }

    return null;
  };

  // Get filtered options for club_location based on selected state
  const getFilteredOptions = (options: FieldOptionDto[]): FieldOptionDto[] => {
    // Only filter if this is the club_location field
    if (customField.fieldNameCode !== "club_location") {
      return options;
    }

    const selectedState = getStateFromLocationData();
    const selectedStateValue = values?.selectedState;

    // If no state is selected, return empty array (hide options until state is selected)
    if (!selectedState || !options) {
      return [];
    }

    // If "Other" is selected, show ALL locations (sorted alphabetically)
    if (selectedStateValue === "OTHER" || selectedState === "OTHER") {
      return options.sort((a, b) => a.display.localeCompare(b.display));
    }

    // Filter options by state and sort alphabetically
    return options
      .filter(
        (option) => option.state && option.state.toUpperCase() === selectedState.toUpperCase(),
      )
      .sort((a, b) => a.display.localeCompare(b.display));
  };

  const renderField = () => {
    const isRequired = customField.required !== false; // Default to true for backward compatibility

    switch (customField.fieldType) {
      case "TEXT":
        return (
          <Text field={fieldName} label={customField.fieldNameDisplay} required={isRequired} />
        );

      case "DROPDOWN": {
        const filteredOptions = getFilteredOptions(customField.fieldOptions || []);

        // For club_location field, hide completely when no state is selected
        if (customField.fieldNameCode === "club_location" && filteredOptions.length === 0) {
          return null; // Hide the entire field when no state is selected
        }

        // Clear the field value if current selection is not in filtered options
        const currentValue = values[fieldName];
        if (currentValue && customField.fieldNameCode === "club_location") {
          const isCurrentValueValid = filteredOptions.some(
            (option) => option.value === currentValue,
          );
          if (!isCurrentValueValid) {
            // Clear the field if the current value is not valid for the selected state
            setTimeout(() => form.change(fieldName, ""), 0);
          }
        }

        const selectOptions = filteredOptions.map((option) => ({
          label: option.display,
          value: option.value,
        }));

        return (
          <Select
            field={fieldName}
            label={customField.fieldNameDisplay}
            options={selectOptions}
            required={isRequired}
            includeBlank={true}
            formControlProps={{ style: { width: "100%" } }}
          />
        );
      }

      case "PHONE_NUMBER":
        return (
          <Text
            field={fieldName}
            label={customField.fieldNameDisplay}
            type="tel"
            required={isRequired}
          />
        );

      case "BOOLEAN":
        return (
          <Checkbox field={fieldName} label={customField.fieldNameDisplay} defaultChecked={false} />
        );

      default:
        return null;
    }
  };

  return <div className="custom-field-wrapper">{renderField()}</div>;
};

// Component for rendering all custom fields (Crunch Fitness integration)
const OrganizationCustomFields = ({
  privacyCenter,
  form,
  values,
}: {
  privacyCenter?: PrivacyCenterDto;
  form: any;
  values: any;
}) => {
  // Handle new multiple custom fields
  if (privacyCenter?.customFields && privacyCenter.customFields.length > 0) {
    // Address field codes that should respect showExtendedAddressFormForRequests setting
    const addressFieldCodes = ["address1", "address2", "city", "state", "zipCode", "country"];

    // Determine which flag set to use based on form type (YPC == Opt-Out flows)
    const isYpcForm = values?.dataRequestType?.formType === FormType.YPC;

    // Filter out address fields using the correct flag
    let filteredCustomFields = privacyCenter.customFields;
    const showAddress = showAddressorPhoneNumberForm(privacyCenter, "address", isYpcForm);
    if (!showAddress) {
      filteredCustomFields = filteredCustomFields.filter(
        (field) => !addressFieldCodes.includes(field.fieldNameCode),
      );
    }

    // Filter out phone number fields using the correct flag
    const showPhone = showAddressorPhoneNumberForm(privacyCenter, "phoneNumber", isYpcForm);
    if (!showPhone) {
      filteredCustomFields = filteredCustomFields.filter(
        (field) => field.fieldNameCode !== "phoneNumber",
      );
    }

    // Check if we have any minor fields in the filtered list
    const hasMinorFields = filteredCustomFields.some(
      (field) =>
        field.fieldNameCode === "minor_first_name" || field.fieldNameCode === "minor_last_name",
    );

    // Separate minor and non-minor fields from the filtered list
    const nonMinorFields = filteredCustomFields.filter(
      (field) =>
        field.fieldNameCode !== "minor_first_name" && field.fieldNameCode !== "minor_last_name",
    );
    const minorFields = filteredCustomFields.filter(
      (field) =>
        field.fieldNameCode === "minor_first_name" || field.fieldNameCode === "minor_last_name",
    );

    return (
      <>
        {/* Render non-minor fields first */}
        {nonMinorFields.map((customField) => (
          <CustomFieldComponent
            key={customField.fieldNameCode}
            customField={customField}
            form={form}
            values={values}
          />
        ))}

        {/* Add minor checkbox if minor fields exist */}
        {hasMinorFields && (
          <Checkbox
            field="isMinorRequest"
            defaultChecked={false}
            label="I am submitting this request on behalf of a minor"
            afterChange={(checked) => {
              // Clear minor fields when unchecking
              if (!checked) {
                minorFields.forEach((field) => {
                  form.change(`custom_${field.fieldNameCode}`, "");
                });
              }
            }}
          />
        )}

        {/* Render minor fields only if checkbox is checked */}
        {hasMinorFields &&
          values?.isMinorRequest &&
          minorFields.map((customField) => (
            <CustomFieldComponent
              key={customField.fieldNameCode}
              customField={customField}
              form={form}
              values={values}
            />
          ))}
      </>
    );
  }

  return null;
};

// Helper function to check if there's a required phone number custom field
const hasRequiredPhoneNumberCustomField = (
  privacyCenter?: PrivacyCenterDto,
  dataRequestType?: RegulationRequestTypeDto,
): boolean => {
  if (!privacyCenter?.customFields || !dataRequestType) {
    return false;
  }

  const isYpcForm = dataRequestType.formType === FormType.YPC;
  const showPhone = showAddressorPhoneNumberForm(privacyCenter, "phoneNumber", isYpcForm);
  if (!showPhone) return false;

  return privacyCenter.customFields.some(
    (field) =>
      field.fieldNameCode === "phoneNumber" &&
      field.fieldType === "PHONE_NUMBER" &&
      field.required !== false,
  );
};

type NewRequestFormProps = {
  handleSubmit: () => void;
  onCancel: () => void;
  dataRequestTypeOptions: RegulationRequestTypeDto[];
  locationOptions: Array<{
    label: string;
    value: string;
    children?: Array<{ label: string; value: string }>;
  }>;
  allowHrRequest: boolean;
  values: any;
  submitting: boolean;
  form: any;
  organization: OrganizationDto;
  privacyCenter?: PrivacyCenterDto;
};

export const NewRequestForm: React.FC<NewRequestFormProps> = ({
  handleSubmit,
  dataRequestTypeOptions,
  locationOptions,
  allowHrRequest,
  onCancel,
  values,
  submitting,
  form,
  organization,
  privacyCenter,
}) => {
  // Get data map to check for sensitive PICs collection
  const { data: dataMap } = useDataMap(organization.id);

  // Enhanced filtering with slug-based optimizations and memoization
  const getRelevantRegionMappings = useCallback((): SimplifiedRegionRequestTypeDto[] => {
    const mapping = privacyCenter?.regionRequestTypeMap;
    if (!mapping) {
      return [];
    }

    // Prefer explicit state/region code selections when available
    if (values.selectedState) {
      const directMatch = mapping[values.selectedState];
      if (directMatch) {
        return [directMatch];
      }

      const regulationMatches = Object.values(mapping).filter(
        (entry) => entry.regulation === values.selectedState,
      );
      if (regulationMatches.length > 0) {
        return regulationMatches;
      }
    }

    if (values.region) {
      if (values.region === "USA") {
        // State should be selected to narrow; without it we cannot determine mapping
        return [];
      }

      const regionGroupMatches = Object.values(mapping).filter(
        (entry) => entry.regionGroup === values.region,
      );
      if (regionGroupMatches.length > 0) {
        return regionGroupMatches;
      }

      const regulationMatches = Object.values(mapping).filter(
        (entry) => entry.regulation === values.region,
      );
      if (regulationMatches.length > 0) {
        return regulationMatches;
      }
    }

    // Fallback: return full list so no additional filtering is applied
    return Object.values(mapping);
  }, [privacyCenter?.regionRequestTypeMap, values.region, values.selectedState]);

  const filteredOptions = useMemo(() => {
    const result = dataRequestTypeOptions
      .filter((requestType) => {
        // Filter by regulation based on selected region group
        const requestRegulation = requestType.regulationLaw?.abbreviation;

        // Map region groups to their expected regulations
        let expectedRegulations: string[] = [];

        if (values.region === "USA" && values.selectedState) {
          // For US states, selectedState is now a region code (e.g., "US-GA", "US-CA", "OTHER")
          // We need to find the regulation for this region code using the regionRequestTypeMap
          if (privacyCenter?.regionRequestTypeMap) {
            const regionMapping = privacyCenter.regionRequestTypeMap[values.selectedState];
            if (regionMapping?.regulation) {
              expectedRegulations = [regionMapping.regulation];
            }
          }
        } else if (values.region === "USA" && !values.selectedState) {
          // If United States is selected but no state is chosen, don't show any request types
          return false;
        } else {
          // Map region groups to their regulations using regionRequestTypeMap
          if (privacyCenter?.regionRequestTypeMap) {
            switch (values.region) {
              case "EEA_UK":
                // Find all regulations in the EEA_UK region group
                expectedRegulations = Object.values(privacyCenter.regionRequestTypeMap)
                  .filter((mapping) => mapping.regionGroup === "EEA_UK")
                  .map((mapping) => mapping.regulation)
                  .filter((reg) => reg); // Remove null/undefined
                break;
              case "CANADA":
                // Find all regulations in the CANADA region group
                expectedRegulations = Object.values(privacyCenter.regionRequestTypeMap)
                  .filter((mapping) => mapping.regionGroup === "CANADA")
                  .map((mapping) => mapping.regulation)
                  .filter((reg) => reg);
                break;
              case "OTHER":
                // Find all regulations in the OTHER region group
                expectedRegulations = Object.values(privacyCenter.regionRequestTypeMap)
                  .filter((mapping) => mapping.regionGroup === "OTHER")
                  .map((mapping) => mapping.regulation)
                  .filter((reg) => reg);
                break;
              default:
                // Fallback: treat as regulation name for backward compatibility
                expectedRegulations = [values.region || "VOLUNTARY"];
            }
          } else {
            // Fallback if no regionRequestTypeMap
            expectedRegulations = [values.region || "VOLUNTARY"];
          }
        }

        // Check if the request type's regulation matches any expected regulation
        if (expectedRegulations.length > 0 && requestRegulation) {
          if (!expectedRegulations.includes(requestRegulation)) {
            return false;
          }
        } else if (expectedRegulations.length > 0) {
          // We have expected regulations but the request type has no regulation
          return false;
        }

        // Additional guard: ensure workflow type is available for the selected region/state
        if (privacyCenter?.regionRequestTypeMap) {
          const relevantMappings = getRelevantRegionMappings();

          if (relevantMappings.length > 0) {
            const isWorkflowAllowed = relevantMappings.some((entry) => {
              const workflows =
                requestType.formType === FormType.YPC
                  ? entry.optOutRequestTypes
                  : entry.dsarRequestTypes;

              if (!workflows || workflows.length === 0) {
                return false;
              }

              const regulationMatches =
                entry.regulation && requestRegulation
                  ? entry.regulation === requestRegulation
                  : true;

              if (!regulationMatches) {
                return false;
              }

              return workflows.includes(requestType.workflowType);
            });

            if (!isWorkflowAllowed) {
              return false;
            }
          }
        }

        // Exclude requests based on DSAR Workflows setup
        if (
          requestType.workflowType === WorkflowType.LIMIT &&
          privacyCenter?.optOutFormOptions?.limit === false
        ) {
          return false;
        }
        if (
          requestType.workflowType === WorkflowType.PROFILING_OPT_OUT &&
          privacyCenter?.optOutFormOptions?.profilingOptOut === false
        ) {
          return false;
        }

        // Exclude US withdraw consent if organization doesn't collect sensitive PICs
        // Similar to how limit requests are filtered based on sensitive PIC collection
        if (
          requestType.workflowType === WorkflowType.US_WITHDRAW_CONSENT &&
          !shouldShowUSWithdrawConsent(dataMap)
        ) {
          return false;
        }

        // Exclude only GPC and browser opt-out request types from the admin New Request form
        if (
          requestType.workflowType === WorkflowType.BROWSER_OPT_OUT ||
          requestType.workflowType === WorkflowType.GPC_OPT_OUT
        ) {
          return false;
        }

        return true;
      })
      .map((requestType) => ({
        label: requestType.shortLabel,
        value: requestType,
        workflow: requestType.workflowType,
        slug: requestType.requestTypeSlug, // Add slug for efficient operations
        key: requestType.requestTypeSlug, // Use slug as React key for better reconciliation
      }))
      .filter((option, index, array) => {
        // Simple deduplication: keep only the first occurrence of each shortLabel
        return array.findIndex((item) => item.label === option.label) === index;
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return result;
  }, [
    dataRequestTypeOptions,
    values.region,
    values.selectedState,
    privacyCenter,
    getRelevantRegionMappings,
    dataMap,
  ]);

  const descriptionLabel = useMemo(
    () => getDescriptionLabel(values?.dataRequestType),
    [values?.dataRequestType],
  );
  const descriptionRequired = useMemo(
    () =>
      isDescriptionRequired(values?.submissionMethod, values?.sendEmailVerification) &&
      !!descriptionLabel,
    [descriptionLabel, values?.sendEmailVerification, values?.submissionMethod],
  );

  const sendEmailVerificationAdditionalProps: CheckboxProps = {};
  const skipEmailVerification = values.dataRequestType && isSkipHiddenType(values.dataRequestType);
  const requireEmailVerification =
    values.dataRequestType && isEmailVerificationRequired(values.dataRequestType);
  const sendEmailVerificationDisabled = skipEmailVerification || requireEmailVerification;

  if (skipEmailVerification) {
    sendEmailVerificationAdditionalProps.checked = false;
    sendEmailVerificationAdditionalProps.value = false;
  } else if (requireEmailVerification) {
    sendEmailVerificationAdditionalProps.checked = true;
    sendEmailVerificationAdditionalProps.value = true;
  }

  const hasRequiredPhoneField = hasRequiredPhoneNumberCustomField(
    privacyCenter,
    values.dataRequestType,
  );

  return (
    <form onSubmit={handleSubmit} className="flex-row flex--expand new-request-form pt-xs">
      <div className="flex-col">
        <div className="flex-row flex--expand">
          <Text field="subjectFirstName" label="First Name" autoFocus required />
          <Text field="subjectLastName" label="Last Name" required />
        </div>

        <Text field="subjectEmailAddress" label="Email Address" type="email" required />

        {privacyCenter?.hideLocationDropdownOnRequestForm === false && (
          <Select
            field="region"
            label="Region"
            options={locationOptions}
            includeBlank={true}
            onChange={() => {
              // Clear dependent fields when region changes
              if (values.dataRequestType) {
                form.change("dataRequestType", undefined);
              }
              if (values.selectedState) {
                form.change("selectedState", undefined);
              }
              form.change("hrRequest", undefined);
              // Clear club_location field when region changes
              form.change("custom_club_location", "");
            }}
            required
          />
        )}

        {values.region === "USA" && (
          <Select
            field="selectedState"
            label="State"
            options={locationOptions.find((option) => option.value === "USA")?.children || []}
            includeBlank={true}
            required
            onChange={() => {
              // Clear request type when state changes
              if (values.dataRequestType) {
                form.change("dataRequestType", undefined);
              }
              form.change("hrRequest", undefined);
              // Clear club_location field when state changes to trigger filtering
              form.change("custom_club_location", "");
            }}
          />
        )}

        <Select
          field="dataRequestType"
          label="Request Type"
          disabled={
            (!values.region || (values.region === "USA" && !values.selectedState)) &&
            privacyCenter?.hideLocationDropdownOnRequestForm === false
          }
          options={filteredOptions}
          required
        />

        {(((values.selectedState == "US-CA" || values.region == "CCPA") &&
          values.dataRequestType?.workflowType !== WorkflowType.LIMIT) ||
          values.region == "EEA_UK") &&
          allowHrRequest && (
            <Checkbox
              field="hrRequest"
              defaultChecked={false}
              label={
                <span className="text-t2">
                  This is an HR request (current/former employee or job applicant)
                </span>
              }
            />
          )}

        {/* Unified Custom Field Logic - Show appropriate custom field based on request type */}
        {(() => {
          // Determine which custom field to show based on request type
          const isYPCForm = values.dataRequestType?.formType === FormType.YPC;
          const isDSARForm = values.dataRequestType?.formType === FormType.DSAR;

          // For DSAR forms, use the legacy custom field
          if (isDSARForm && organization.customFieldEnabled) {
            return (
              <Text
                field="organizationCustomInput"
                required={false}
                label={
                  <>
                    {organization.customFieldLabel}
                    {organization.customFieldHelp && (
                      <>
                        {" "}
                        <HelpPopover label={organization.customFieldHelp} />
                      </>
                    )}
                  </>
                }
              />
            );
          }

          // For YPC forms, use the opt-out custom field from organization settings
          if (isYPCForm && organization?.customOptOutFieldEnabled) {
            const label = organization.customOptOutFieldLabel || "Custom Field";
            return (
              <Text
                field="organizationCustomInput"
                required={false}
                label={
                  <>
                    {label}
                    {organization.customOptOutFieldHelp && (
                      <>
                        {" "}
                        <HelpPopover label={organization.customOptOutFieldHelp} />
                      </>
                    )}
                  </>
                }
              />
            );
          }

          return null;
        })()}

        {/* Address fields are now handled by OrganizationCustomFields component */}

        {/* Crunch Fitness Custom Fields Integration */}
        <OrganizationCustomFields privacyCenter={privacyCenter} form={form} values={values} />

        <Select
          field="submissionMethod"
          label="Submission Method"
          options={SUBMISSION_METHOD_OPTIONS}
          required
        />

        {values.submissionMethod === "Telephone" && !hasRequiredPhoneField && (
          <Text field="subjectPhoneNumber" label="Telephone Number" type="tel" />
        )}

        {values.submissionMethod === "Email" && (
          <Checkbox
            {...sendEmailVerificationAdditionalProps}
            field="sendEmailVerification"
            defaultChecked={true}
            disabled={sendEmailVerificationDisabled}
            label={
              <>
                Send verification email to requester{" "}
                <ToolTip
                  text={
                    skipEmailVerification
                      ? "This request type does not require email verification."
                      : requireEmailVerification
                      ? "More information is needed for this request and will be collected during email verification."
                      : "Uncheck this box if the consumer emailed you directly from the email address associated with the request and email verification is unnecessary."
                  }
                  maxWidth="300px"
                  inline={true}
                >
                  <HelpIcon className="tooltip align-middle" fontSize="small" color="primary" />
                </ToolTip>
              </>
            }
          />
        )}

        {values.dataRequestType?.workflowType === WorkflowType.APPEAL && (
          <>
            <label>
              Appeal ID
              <Text field="appealId" required className="mb-md mt-sm" />
            </label>
          </>
        )}

        {descriptionRequired && (
          <>
            <label>
              {descriptionLabel}
              <Text field="description" required isMultiLine className="mb-md mt-sm" />
            </label>
          </>
        )}

        <DatePicker field="requestDate" label="Request Date" required />

        {values.hrRequest && (
          <Callout className="text-t2" variant={CalloutVariant.Secondary}>
            This request will be submitted as a current/former employee or job applicant.
            <br />
            Please{" "}
            <strong>
              submit a separate request regarding personal data related to a customer account
            </strong>{" "}
            or uncheck the box above.
          </Callout>
        )}

        <div className="fill justify-end">
          {onCancel && (
            <Button
              color="primary"
              disabled={submitting}
              onClick={(e) => {
                e.preventDefault();
                onCancel();
              }}
            >
              Cancel
            </Button>
          )}

          <LoadingButton
            loading={submitting}
            type="submit"
            variant="contained"
            color="primary"
            className="ml-md"
          >
            Create
          </LoadingButton>
        </div>
      </div>
    </form>
  );
};
